package dev.kuml.portal.web.editor

/**
 * Wire-format type discriminator for the collaboration WebSocket's binary frames -- mirrors
 * `kuml-portal-server`'s `dev.kuml.portal.server.collab.CollabWireProtocol` byte-for-byte (see that
 * server module's `CollabRoomRegistry`'s class KDoc "Presence (Awareness) design" for the full
 * wire-shape rationale). Pure, DOM-free logic so the frame parsing itself is directly
 * unit-testable (see `CollabFrameTest`), independently of the WebSocket/DOM glue that actually
 * receives the bytes (`CollabClient.kt`).
 *
 * Every binary frame, both directions: `[1 byte kind][payload]`.
 * - [KIND_CRDT]'s payload is a raw Loro update/snapshot blob -- this v1 web client never parses it
 *   (see [isRefreshSignal]'s KDoc for why).
 * - [KIND_PRESENCE]'s server-to-client payload is `[16-byte connection token][opaque bytes]`; an
 *   empty trailing payload means "this token has no/cleared presence" (see [decodePresence]). R-28:
 *   those opaque trailing bytes are this client's own [PresencePayload] JSON -- [decodePresence]
 *   still treats them as opaque (just a UTF-8 [CollabPresenceEvent.payload] string); decoding the
 *   JSON itself is `EditorScreen.kt`'s job (see [CollabPresenceEvent]'s own KDoc for why).
 */
public object CollabFrame {
    public const val KIND_CRDT: Byte = 0
    public const val KIND_PRESENCE: Byte = 1

    /**
     * R-16: identity attribution for the presence roster (server-side
     * `dev.kuml.portal.server.collab.CollabRoutes`' `FRAME_KIND_PRESENCE_IDENTITY`, added in the
     * Teams collaboration-integration wave, 2026-07-28 -- this client never decoded it until now).
     * Two shapes on the wire, both `[1-byte kind][16-byte token][16-byte userId]` plus an OPTIONAL
     * trailing UTF-8 display name:
     *  - Snapshot (already-attached peers, sent once on join): exactly 33 bytes total, no name --
     *    the registry never resolves one for this shape (see the server's own KDoc).
     *  - Announce (a newly attached peer's own identity, broadcast to everyone else): more than 33
     *    bytes, the remainder is the sender's UTF-8 display name.
     */
    public const val KIND_PRESENCE_IDENTITY: Byte = 2

    /** The frame's 1-byte kind discriminator, or `null` for an empty frame (no kind byte at all). */
    public fun kindOf(frame: ByteArray): Byte? = frame.firstOrNull()

    /**
     * `true` for any well-formed [KIND_CRDT] frame -- this v1 web client deliberately never parses
     * the Loro payload itself (no client-side CRDT/Loro library exists in this module -- see the
     * plan's scope notes): a CRDT frame is treated purely as a "something changed server-side,
     * re-fetch the rendered diagram" signal, handled by re-calling `PortalApi.renderProject` (see
     * `EditorScreen.kt`'s `refreshDiagramFromServer`).
     */
    public fun isRefreshSignal(frame: ByteArray): Boolean = kindOf(frame) == KIND_CRDT

    /**
     * Decodes a server-to-client [KIND_PRESENCE] frame -- `[1-byte kind][16-byte token][opaque
     * bytes]`. Returns `null` -- never throws -- for anything that isn't a well-formed presence
     * frame: a wrong kind byte, or fewer than 17 total bytes (no room for the 16-byte token). An
     * empty trailing payload (exactly 17 total bytes) decodes to [CollabPresenceEvent.present] =
     * `false` -- the server's "this token has no/cleared presence" convention, reused for both an
     * explicit clear and a "this connection left" announcement, so a caller never needs to special
     * case either -- [CollabPresenceEvent.payload] is `""` in that case (R-28).
     */
    public fun decodePresence(frame: ByteArray): CollabPresenceEvent? {
        if (kindOf(frame) != KIND_PRESENCE) return null
        if (frame.size < 17) return null
        val tokenHex = frame.copyOfRange(1, 17).joinToString("") { toHexByte(it) }
        val present = frame.size > 17
        val payload = frame.copyOfRange(17, frame.size).decodeToString()
        return CollabPresenceEvent(tokenHex, present, payload)
    }

    /**
     * Decodes a [KIND_PRESENCE_IDENTITY] frame -- `[1][16-byte token][16-byte userId]` plus an
     * optional trailing UTF-8 display name (see this object's KDoc for the two wire shapes).
     * Returns `null` -- never throws -- for a wrong kind byte or fewer than 33 total bytes (no
     * room for both 16-byte fields).
     */
    public fun decodeIdentity(frame: ByteArray): CollabIdentityEvent? {
        if (kindOf(frame) != KIND_PRESENCE_IDENTITY) return null
        if (frame.size < 33) return null
        val tokenHex = frame.copyOfRange(1, 17).joinToString("") { toHexByte(it) }
        val userId = toUuidString(frame, 17)
        val displayName = if (frame.size > 33) frame.copyOfRange(33, frame.size).decodeToString() else ""
        return CollabIdentityEvent(tokenHex, userId, displayName)
    }

    /**
     * Decodes 16 big-endian bytes starting at [offset] into the canonical `8-4-4-4-12` UUID text
     * form (lowercase) -- the counterpart to the server's `kotlin.uuid.Uuid.toByteArray()`, which
     * `CollabRoutes`/`CollabRoomRegistry` use to serialize both the connection token and the
     * sender's `userId` onto the wire.
     */
    public fun toUuidString(
        raw: ByteArray,
        offset: Int,
    ): String {
        val hex = (offset until offset + 16).joinToString("") { toHexByte(raw[it]) }
        return buildString {
            append(hex, 0, 8)
            append('-')
            append(hex, 8, 12)
            append('-')
            append(hex, 12, 16)
            append('-')
            append(hex, 16, 20)
            append('-')
            append(hex, 20, 32)
        }
    }

    private fun toHexByte(byte: Byte): String {
        val value = byte.toInt() and 0xFF
        return HEX_DIGITS[value ushr 4].toString() + HEX_DIGITS[value and 0x0F].toString()
    }

    private val HEX_DIGITS = "0123456789abcdef"
}

/** One presence update decoded from the wire -- see [CollabFrame.decodePresence]. [payload] is the
 * raw UTF-8 opaque trailing bytes (`""` when [present] is `false`) -- R-28: still undecoded JSON at
 * this layer, see [CollabFrame]'s class KDoc "opaque" for why `CollabClient.kt` hands it through
 * unparsed rather than decoding [PresencePayload] itself. */
public data class CollabPresenceEvent(
    val tokenHex: String,
    val present: Boolean,
    val payload: String = "",
)

/** One identity update decoded from the wire -- see [CollabFrame.decodeIdentity]. [displayName]
 * is `""` for the snapshot shape (see [CollabFrame.KIND_PRESENCE_IDENTITY]'s KDoc). */
public data class CollabIdentityEvent(
    val tokenHex: String,
    val userId: String,
    val displayName: String,
)
